import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha, Box, Typography, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL } from "../../../../theme/tokens";
import { TOUCH_TARGET_SX } from "../../touchTarget";

/** One labelled figure in a row's metric strip. `value` is a node so callers can pass a shared `Cells/*`. */
export type DecisionRowMetric = {
	/** Stable key — also the testid suffix, so a metric can be asserted without depending on its position. */
	key: string;
	/** Already-translated micro-label above the value. */
	label: string;
	value: ReactNode;
	/** Overrides the derived `<listTestId>-<key>-<rowId>` — for a caller with established selectors. */
	testId?: string;
};

export type DecisionRowItem = {
	id: string | number;
	/** The identity line — already-translated / already-formatted by the caller. */
	title: string;
	/** Annotation chips beside the title (compose `MetaChip`s); they must not contain a real `<button>`. */
	chips?: ReactNode;
	/** The row's headline figure, trailing the title line (a cost, a score, a count). */
	trailing?: ReactNode;
	/** One short line under the title saying why this row stands out — only meaningful on the lead row. */
	note?: string;
	metrics: DecisionRowMetric[];
	/** Overrides the derived `<listTestId>-row-<id>`. */
	rowTestId?: string;
	/** Overrides the derived `<listTestId>-select-<id>` on the trailing chevron button. */
	selectTestId?: string;
};

type Props = {
	rows: DecisionRowItem[];
	/**
	 * When provided, each row becomes selectable — the whole row is a pointer target and the trailing
	 * chevron is the row's one real focusable control, so list semantics hold and no `<button>` ever
	 * wraps the row's own block content (which would be invalid markup).
	 */
	onSelect?: (id: string | number) => void;
	/** Disables selection (a pick in flight, an expired card) without changing the layout. */
	selectionDisabled?: boolean;
	/** Gets the brand rail + renders its `note`. Pass the id the caller's own ordering ranks first. */
	leadRowId?: string | number;
	/**
	 * Metric columns per row. Every row uses the same count, which is what keeps figures aligned down
	 * the list — the alignment a wide table buys, without a table's horizontal overflow. Default 4.
	 */
	metricColumns?: number;
	/** Accessible name for the list itself (e.g. "pending pickups"). */
	ariaLabel?: string;
	dataTestId?: string;
};

/**
 * A vertical list of comparable options, each carrying its own labelled metric strip — the shape to reach
 * for when someone has to CHOOSE between a handful of candidates on a narrow surface (a chat panel, a
 * popover, a phone), and the numbers behind the choice have to be visible rather than a column away.
 *
 * Why a list and not a `SystemDataGrid`: past ~4 columns a table on a ~520px surface pushes its trailing
 * columns into a horizontal scroll, and the columns that get pushed out are exactly the ones the decision
 * needs. Stacking each option into two tiers — identity + a fixed-column metric grid — keeps every figure
 * on screen AND still aligned down the list, because each row lays its metrics out on the same grid. Use
 * `SystemDataGrid` instead whenever the surface is wide enough, or the data wants sorting, filtering,
 * export or column-picking; this owns the narrow, small-N, one-decision case.
 *
 * Purely presentational: it fetches nothing, formats nothing, and translates nothing. Callers pass
 * already-formatted nodes — ideally the shared `UIToolkit/Cells/*` renderers — so a figure reads
 * identically here and in the full grid it came from.
 */
export const DecisionRowList: FC<Props> = ({ rows, onSelect, selectionDisabled = false, leadRowId, metricColumns = 4, ariaLabel, dataTestId }) => {
	const theme = useTheme();

	if (rows.length === 0) return null;

	// Two separate questions. `selectable` is STRUCTURAL — does this list have a per-row action at all —
	// and decides whether the chevron (the row's only focusable control, and its `selectTestId` anchor)
	// exists. `interactive` is BEHAVIOURAL: whether that action currently fires. Folding the two together
	// meant an in-flight or expired picker silently dropped its buttons and test IDs instead of just
	// going inert.
	const selectable = !!onSelect;
	const interactive = selectable && !selectionDisabled;

	return (
		<Box
			component="ul"
			aria-label={ariaLabel}
			data-testid={dataTestId}
			sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}
		>
			{rows.map((row) => {
				const isLead = leadRowId !== undefined && row.id === leadRowId;
				return (
					<Box
						component="li"
						key={row.id}
						data-testid={row.rowTestId ?? (dataTestId ? `${dataTestId}-row-${row.id}` : undefined)}
						onClick={interactive ? () => onSelect?.(row.id) : undefined}
						sx={{
							minWidth: 0,
							px: 1.25,
							pt: 1.25,
							pb: 1.25,
							borderRadius: 1,
							border: `1px solid ${NEUTRAL[200]}`,
							// The lead rail is the caller's ordering made visible — it says "this is the one the
							// system ranks first", so it is information, not decoration.
							borderLeft: `3px solid ${isLead ? theme.palette.primary.main : NEUTRAL[200]}`,
							backgroundColor: isLead ? alpha(theme.palette.primary.main, 0.035) : "transparent",
							transition: `background-color ${MOTION.DURATION.HOVER}ms ease, border-color ${MOTION.DURATION.HOVER}ms ease`,
							...(interactive
								? {
										cursor: "pointer",
										"@media (hover: hover) and (pointer: fine)": {
											"&:hover": { backgroundColor: isLead ? alpha(theme.palette.primary.main, 0.07) : NEUTRAL[50], borderColor: NEUTRAL[400] },
										},
										"&:active": { backgroundColor: NEUTRAL[150] },
										// Driven by :focus-within because the real focusable control is the trailing
										// chevron button — the row itself never takes a tabstop or a role.
										"&:focus-within": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
									}
								: {}),
							"@media (prefers-reduced-motion: reduce)": { transition: "none" },
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
							<Typography
								sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
							>
								{row.title}
							</Typography>
							{row.chips}
							{(row.trailing || selectable) && (
								<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
									{row.trailing}
									{selectable && (
										// The row's one real focusable control: keyboard and screen readers act on this
										// (announced with the row's title), while the row's onClick stays a pointer
										// convenience. Carries the finger target itself, since it is the only hit area
										// a keyboard user can aim at.
										<Box
											component="button"
											type="button"
											aria-label={row.title}
											disabled={selectionDisabled}
											data-testid={row.selectTestId ?? (dataTestId ? `${dataTestId}-select-${row.id}` : undefined)}
											onClick={(event) => {
												event.stopPropagation();
												onSelect?.(row.id);
											}}
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												p: 0.25,
												...TOUCH_TARGET_SX,
												border: "none",
												background: "none",
												borderRadius: 1,
												color: INK.MUTED,
												cursor: interactive ? "pointer" : "default",
												"&:disabled": { opacity: 0.5 },
												// The row draws the visible ring via :focus-within, so this doesn't double it.
												"&:focus": { outline: "none" },
											}}
										>
											<ChevronRightIcon sx={{ fontSize: 18, display: "block" }} />
										</Box>
									)}
								</Box>
							)}
						</Box>

						{isLead && row.note && (
							<Typography sx={{ mt: 0.25, fontSize: FONT_SIZE.CAPTION, fontWeight: 600, color: theme.palette.primary.main }}>{row.note}</Typography>
						)}

						{row.metrics.length > 0 && (
							// <div>-grouped <dl>: each label/value pair is one grid cell, so the pairs stay
							// semantically associated while the grid does the aligning.
							<Box component="dl" sx={{ m: 0, mt: 1, display: "grid", gridTemplateColumns: `repeat(${metricColumns}, minmax(0, 1fr))`, gap: "4px 8px" }}>
								{row.metrics.map((metric) => (
									<Box key={metric.key} sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }}>
										<Box
											component="dt"
											sx={{
												fontSize: FONT_SIZE.LABEL_MICRO,
												fontWeight: 500,
												letterSpacing: LETTER_SPACING.LABEL,
												textTransform: "uppercase",
												color: NEUTRAL[500],
												whiteSpace: "nowrap",
											}}
										>
											{metric.label}
										</Box>
										<Box
											component="dd"
											data-testid={metric.testId ?? (dataTestId ? `${dataTestId}-${metric.key}-${row.id}` : undefined)}
											sx={{
												m: 0,
												fontSize: FONT_SIZE.SECONDARY,
												color: INK.SECONDARY,
												fontVariantNumeric: "tabular-nums",
												minWidth: 0,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												// Shrink-wrap the value so it starts under its label. Several shared `Cells/*`
												// renderers center themselves internally (OvertimeCell sets
												// `justifyContent: center`, for the centered DataGrid columns they were built
												// for); as a block child that fills this cell, such a renderer floats to the
												// middle while its plain-text neighbours sit at the start. Making the cell a
												// start-aligned flex line sizes the child to its content, so its own centering
												// becomes a no-op and every column reads down one edge.
												display: "flex",
												alignItems: "baseline",
												justifyContent: "flex-start",
												// Keeps the ellipsis working now that the text is a flex item, not a block.
												"& > *": { minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" },
											}}
										>
											{metric.value}
										</Box>
									</Box>
								))}
							</Box>
						)}
					</Box>
				);
			})}
		</Box>
	);
};
